'use client';
import dayjs from 'dayjs';
import { Filter } from '@/components/atoms/Display';
import Schedule from '@/components/molecules/Schedule/Schedule';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Option, OptionGroup, Select } from '@/ui-components/atoms/Inputs/Select';
import ScheduleGroup from '@/components/molecules/Schedule/ScheduleGroup';
import EmptyTabsMessage from '@/components/contentful/ContextualComponent/Sports/EmptyTabsMessage';
import { useState } from 'react';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useViewerTimeZone } from '@/hooks/useViewerTimeZone';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
    shows?: SiteChannelShow[];
    renderedAt: number;
}
export default function ChannelSchedule({ shows, renderedAt }: Props): React.JSX.Element {
    const timeZone = useViewerTimeZone();
    const now = dayjs(renderedAt).tz(timeZone);
    const [selectedDay, setSelectedDay] = useState<string>();
    const selectedDayValue = selectedDay ?? now.format('dddd');

    const tempSchedules: {
        filterDay: string;
        startTime: dayjs.Dayjs;
        showName: string;
        description: string;
        isNowPlaying: boolean;
    }[] = [];

    const scheduleNodes: React.ReactElement[] = [];

    const seenTimes = new Set<string>();

    shows?.forEach((show: SiteChannelShow) => {
        show.show_schedules?.forEach((daySchedules) => {
            daySchedules.day_schedules?.forEach((daySchedule) => {
                if (daySchedule) {
                    tempSchedules.push({
                        filterDay: dayjs(daySchedule.start_time).tz(timeZone).format('dddd'),
                        startTime: dayjs(daySchedule.start_time).tz(timeZone),
                        showName: show.show_name,
                        description: show.description,
                        isNowPlaying: now.isBetween(daySchedule.start_time, daySchedule.end_time),
                    });
                }
            });
        });
    });

    // sort the schedule array based on daySchedule.start_time
    tempSchedules.sort((a, b) => {
        return a.startTime.valueOf() - b.startTime.valueOf();
    });

    tempSchedules.forEach((schedule) => {
        const formattedStartTime =
            schedule.startTime.minute() === 0 ? schedule.startTime.format('h a') : schedule.startTime.format('h:mm a');
        const uniqueKey = `${schedule.filterDay}-${formattedStartTime}-${schedule.showName}`;
        if (!seenTimes.has(formattedStartTime + schedule.filterDay)) {
            scheduleNodes.push(
                <Filter
                    key={uniqueKey}
                    conditions={{ day: { type: 'exact', value: schedule.filterDay } }}
                    criteria={{ day: selectedDayValue }}
                >
                    <Schedule
                        scheduleDate={schedule.startTime.toDate()}
                        title={schedule.showName}
                        description={schedule.description}
                        isNowPlaying={schedule.isNowPlaying}
                        timeZone={timeZone}
                    />
                </Filter>,
            );
            seenTimes.add(formattedStartTime + schedule.filterDay);
        }
    });

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = parseInt(now.format('d'));
    const printDays = days.slice(today).concat(days.slice(0, today));

    return scheduleNodes.length ? (
        <>
            <StyledContainer paddingBottom={16}>
                <Grid container ignoreInChromatic>
                    <Grid col={{ sm: 12, lg: 10 }} start={{ lg: 2 }}>
                        <Select
                            label="Select"
                            type="bar"
                            theme="outline"
                            name="select"
                            value={selectedDayValue}
                            placeholder={selectedDayValue}
                            onChange={(element: HTMLInputElement, { value }: { value: string }) =>
                                setSelectedDay(value)
                            }
                        >
                            <OptionGroup>
                                {printDays.map((day, i) => (
                                    <Option key={i} value={day}>
                                        {day}
                                    </Option>
                                ))}
                            </OptionGroup>
                        </Select>
                    </Grid>
                </Grid>
            </StyledContainer>
            <ScheduleGroup>{scheduleNodes}</ScheduleGroup>
        </>
    ) : (
        <EmptyTabsMessage />
    );
}
